import {
  Button, Flex, Select,
} from '@chakra-ui/react';
import React, { BaseSyntheticEvent, FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { isNull, omitBy, pick } from 'lodash';
import TextInput from '../TextInput';
import { Artist, Media, StatusTypes } from '../../types/graphql.generated';
import { CreateArtistMutationVariables } from '../../graphql/artists/generated/createArtistMutation.generated';
import Richtext from '../Richtext';
import MediaInput from '../MediaInput';

interface FormValues {
  name: string,
  description: string,
  locale: string,
  image: Media,
}

interface ArtistFormProps {
  onSubmit: (data: CreateArtistMutationVariables) => void,
  loading?: boolean,
  defaultValues?: Artist,
  withPublish?: boolean,
}

const ArtistForm: FC<ArtistFormProps> = ({
  onSubmit, loading, defaultValues, withPublish,
}) => {
  const {
    register, handleSubmit, formState: { errors }, reset, control, watch,
  } = useForm<FormValues>({
    defaultValues: defaultValues ? pick(omitBy<Artist>(defaultValues, isNull), ['locale', 'name', 'description', 'image']) : undefined,
  });

  const onSave = async (data: FormValues, e?: BaseSyntheticEvent) => {
    const buttonType = (e?.nativeEvent as any).submitter?.value || 'draft';

    await onSubmit({
      ...data,
      image: data.image ? data.image.id : null,
      ...(withPublish ? { status: buttonType === 'publish' ? StatusTypes.PUBLISHED : StatusTypes.DRAFT } : {}),
    });
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <Flex
        flexDir="column"
        gap="5"
        my="5"
        maxW="500px"
      >
        <Select
          isInvalid={!!errors.locale}
          {...register('locale', { required: !defaultValues })}
          placeholder="Sprache auswählen"
        >
          <option value="de">Deutsch</option>
          <option value="en">English</option>
        </Select>
        <TextInput
          isInvalid={!!errors.name}
          {...register('name', { required: !defaultValues })}
          placeholder="Name..."
        >
          Name: *
        </TextInput>
        <Controller
          render={
            ({ field }) => (
              <Richtext
                isInvalid={!!errors.description}
                value={field.value}
                onChange={field.onChange}
              >
                Beschreibung:
              </Richtext>
            )
          }
          control={control}
          name="description"
        />
        <Controller
          render={
            ({ field }) => (
              <MediaInput
                isInvalid={!!errors.image}
                value={field.value || undefined}
                locale={watch('locale')}
                onChange={field.onChange}
              >
                Bild:
              </MediaInput>
            )
          }
          control={control}
          name="image"
        />
      </Flex>
      <Flex gap={4}>
        <Button
          mt="30px"
          type="submit"
          value="draft"
          colorScheme="teal"
          isLoading={loading}
        >
          Speichern
        </Button>
        {withPublish && (
          <Button
            mt="30px"
            value="publish"
            type="submit"
            isLoading={loading}
          >
            Speichern & Veröffentlichen
          </Button>
        )}
      </Flex>
    </form>
  );
};

export default ArtistForm;
